import { Form, useNavigation } from 'react-router';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { ExistingSearchParams } from 'remix-utils/existing-search-params';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import { formatNumber } from '~/common/utils/numberUtil';
import { useMemo } from 'react';
import ProgressButton from '~/common/components/ProgressButton.tsx';
import { maxReachablePageCount } from '~/features/catalog/searchResultWindow';

export interface CatalogLoadMoreButtonProps {
  totalCount: number;
  pageSize: number;
  page: number;
  includeSearchParamInSubmit?: boolean;
}

export function CatalogLoadMoreButton({
  totalCount,
  page,
  pageSize,
  includeSearchParamInSubmit = true
}: Readonly<CatalogLoadMoreButtonProps>) {
  const { t, i18n } = useTranslation();
  const navigation = useNavigation();
  const totalPageCount = Math.ceil(totalCount / pageSize);
  const reachablePageCount = maxReachablePageCount(pageSize);
  const totalPages = Math.min(totalPageCount, reachablePageCount);
  const currentPage = page; // Current page is 0-based
  const hasMorePages = currentPage + 1 < totalPages;
  const isSearchResultWindowReached = totalPageCount > reachablePageCount && currentPage + 1 >= reachablePageCount;

  const isLoading = navigation.state === 'loading';

  const pagingText = useMemo(() => {
    const displayedProductCount = Math.min((page + 1) * pageSize, totalCount);
    return `${displayedProductCount} ${t('von')} ${formatNumber(totalCount, i18n.language)} ${t('Produkte')}`;
  }, [i18n.language, page, pageSize, totalCount, t]);

  return (
    <>
      <div className={'flex flex-col items-center mt-8 mb-4 text-secondary'}>
        <div>{pagingText}</div>
        {isSearchResultWindowReached && (
          <div className={'mt-1 text-center'}>
            {t(
              'Die maximale Anzahl an Artikeln wurde erreicht. Bitte nutzen Sie die Filter, um die Auswahl einzugrenzen.'
            )}
          </div>
        )}
      </div>
      {hasMorePages && (
        <Form className={'flex justify-center mb-8'}>
          {includeSearchParamInSubmit && (
            <ClientOnly>{() => <ExistingSearchParams exclude={['page']} />}</ClientOnly>
          )}
          <input type={'hidden'} name={FORM_FIELDS.Page} value={page + 1} />
          <ProgressButton inProgress={isLoading} type={'submit'} data-testid={'catalog-loadmore-button'}>
            {t('Mehr anzeigen')}
          </ProgressButton>
        </Form>
      )}
    </>
  );
}
